import { cloneDeep, isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { getEmptyRuleGroupInAPIForm } from '@src/shared/utils/eventBuilder';
import EventBox from './EventBox';
import { PlusOutlined } from '@ant-design/icons';
import { MappingWrapper, AddNewEvent } from './styled';
export interface IMappingRulesBuilderProps {
  rules?: CriticalEventRule[][];
  onChange?: (rules: CriticalEventRule[][]) => void;
  highlightEvents?: number[];
  validation?: {
    failed: boolean;
    fields: string[];
    message?: string;
    errorIndex?: Record<number, number[]>;
  };
}

const MappingRulesBuilder = ({
  rules,
  onChange,
  highlightEvents,
  validation,
}: IMappingRulesBuilderProps) => {
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const [highlights, setHighlights] = React.useState<number[]>(highlightEvents || []);
  const [eventRules, setEventRules] = React.useState<CriticalEventRule[][]>([]);

  useEffect(() => {
    setEventRules(cloneDeep(rules || []));
  }, [rules]);

  // autofocus the last one.
  useEffect(() => {
    if (!eventRules) return;

    if (!activeIndex) setActiveIndex(0);
  }, [eventRules, activeIndex]);

  useEffect(() => {
    // by requirements, may highlights multiple events one time.
    if (highlightEvents) {
      setHighlights([...highlightEvents]);
    } else {
      setHighlights([0]);
    }
  }, [highlightEvents]);

  useEffect(() => {
    if (activeIndex !== -1) {
      setHighlights([activeIndex]);
    } else {
      setHighlights([]);
    }
  }, [activeIndex]);

  const deleteEvenItem = (eventIndex: number) => {
    if (!eventRules) return;
    const newEventRules = [...eventRules];
    const eventList = newEventRules[0];
    eventList.splice(eventIndex, 1);

    if (newEventRules[0].length === 0) {
      // if delete the last event, keep one empty event on UI
      const defaultEventRule = getEmptyRuleGroupInAPIForm();
      eventList.push(defaultEventRule);
    }
    // if delete the last event, set the activeIndex to the last event
    setActiveIndex(eventIndex === 0 ? 0 : eventIndex - 1);
    setEventRules(newEventRules);
    onChange?.(newEventRules);
  };

  const addEventItem = () => {
    if (!eventRules) return;
    const newEvent = getEmptyRuleGroupInAPIForm();
    const newEventRules = [...eventRules];
    newEventRules[0].push(newEvent);
    setActiveIndex(newEventRules[0].length - 1);
    setEventRules(newEventRules);
    onChange?.(newEventRules);
  };
  return (
    <MappingWrapper key={activeIndex}>
      {eventRules?.[0]?.map((event, eventOrIndex) => {
        return (
          <EventBox
            errorFields={validation?.fields}
            errorIndexs={validation?.errorIndex?.[eventOrIndex]}
            onClick={() => {
              setActiveIndex(eventOrIndex);
            }}
            onDelete={() => {
              deleteEvenItem(eventOrIndex);
            }}
            key={`EventBox_key_${eventOrIndex}`}
            event={event}
            index={eventOrIndex}
            isHighlight={highlights.includes(eventOrIndex)}
            isActive={activeIndex === eventOrIndex}
            onChange={(event) => {
              eventRules[0][eventOrIndex] = event;
              const newEventRules = cloneDeep(eventRules);
              setEventRules(newEventRules);
              onChange?.(newEventRules);
            }}
          />
        );
      })}
      {/*Since support clone multiple mapped events add new move to out of MappingRulesBuilder*/}
      <AddNewEvent onClick={() => addEventItem()}>
        <PlusOutlined style={{ color: '#0046FF' }} />
        &nbsp;Add Raw Event
      </AddNewEvent>
    </MappingWrapper>
  );
};

export default React.memo(MappingRulesBuilder, isEqual);
